/**
 * Client-side parser + template for the "Importar árbol" flow in Metadatos.
 *
 * The user fills an Excel that mirrors the source document (PDF §6):
 *  - Sheet "Config": key/value (org_slug, domain, comercial_id/nombre, interno_id/nombre)
 *  - Sheet "Árbol": 3 columns [Padre | Comercial | Elemento]
 *
 * From that we derive TWO org-scoped trees + associations, so the user never has
 * to think about ids, two trees or association syntax:
 *  - Padre "1 Experiencias saludables"       → parent commercial node (code 1)
 *  - Comercial "1.01 Salud y Termalismo"     → child commercial node (code 1.01)
 *  - Elemento starting with a number "6.04…" → association to that commercial code
 *  - Elemento plain text "Balneoterapia"     → internal (LIBRE-VIDA) leaf node,
 *                                              created in the internal tree and
 *                                              associated to the current child.
 */

// ---- Payload types (match backend ImpPayload) ------------------------------
type AssocRef = string | { tree: string; id: string };
interface ImpNode {
  id?: string;
  name: string;
  code?: string;
  associated?: AssocRef[];
  children?: ImpNode[];
}
interface ImpTree {
  id: string;
  name: string;
  description?: string;
  base_permission?: 'organization';
  nodes: ImpNode[];
}
export interface ImpPayload {
  orgSlug?: string;
  domain: { id: string; name: string; icon?: string };
  trees: ImpTree[];
}

export function toSlug(s: string): string {
  return String(s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

// A cell that starts with "1", "1.05", "8-", "11." … → a code + the rest as name.
const CODE_RE = /^(\d{1,2}(?:\.\d{1,2})?)[\s.\-]+(.+)$/;
// A bare code cell like "8" (parent ref with no trailing text).
const BARE_CODE_RE = /^(\d{1,2}(?:\.\d{1,2})?)$/;

function parseCoded(raw: string): { code: string; name: string } | null {
  const s = raw.trim();
  const m = CODE_RE.exec(s);
  if (m) return { code: m[1], name: m[2].trim() };
  const b = BARE_CODE_RE.exec(s);
  if (b) return { code: b[1], name: s };
  return null;
}

const cid = (code: string) => 'c' + code.replace(/\./g, '-');

export interface ParseResult {
  payload: ImpPayload;
  stats: { padres: number; hijos: number; internas: number; refs: number; hojaLinks: number };
  warnings: string[];
}

export async function parseMetadataTreeExcel(file: File): Promise<ParseResult> {
  const ExcelJSMod: any = await import('exceljs');
  const ExcelJS = ExcelJSMod.default || ExcelJSMod;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());

  const warnings: string[] = [];
  const cell = (row: any, col: number): string => {
    const v = row.getCell(col).value;
    if (v == null) return '';
    if (typeof v === 'object' && 'text' in v) return String(v.text).trim();
    if (typeof v === 'object' && 'result' in v) return String(v.result).trim();
    return String(v).trim();
  };

  // ---- Config sheet ----
  const cfgWs = wb.getWorksheet('Config') || wb.worksheets[0];
  const cfg: Record<string, string> = {};
  cfgWs?.eachRow({ includeEmpty: false }, (row: any) => {
    const k = cell(row, 1).toLowerCase();
    const v = cell(row, 2);
    if (k) cfg[k] = v;
  });
  const orgSlug = cfg['org_slug'] || cfg['org'] || '';
  const domainId = cfg['domain'] || 'categorization';
  const comercialId = cfg['comercial_id'] || 'comercial';
  const comercialName = cfg['comercial_nombre'] || cfg['arbol_comercial'] || 'Comercial';
  const internoId = cfg['interno_id'] || cfg['interna_id'] || 'interna';
  const internoName = cfg['interno_nombre'] || cfg['arbol_interno'] || 'Interna';
  if (!orgSlug) warnings.push('Config: falta "org_slug".');

  // ---- Árbol sheet (3 columns) ----
  const arbolWs = wb.getWorksheet('Árbol') || wb.getWorksheet('Arbol') || wb.worksheets[1];
  if (!arbolWs) throw new Error('No se encontró la hoja "Árbol".');

  const parents: ImpNode[] = [];
  const leafBySlug = new Map<string, string>();
  const comercialCodes = new Set<string>();
  let currentParent: ImpNode | null = null;
  let currentChild: ImpNode | null = null;
  let refs = 0;
  let hojaLinks = 0;
  let headerSkipped = false;

  arbolWs.eachRow({ includeEmpty: false }, (row: any) => {
    const padre = cell(row, 1);
    const comercial = cell(row, 2);
    const elemento = cell(row, 3);
    // Skip the header row (Padre/Comercial/Elemento).
    if (!headerSkipped && /padre/i.test(padre) && /comercial/i.test(comercial)) {
      headerSkipped = true;
      return;
    }
    headerSkipped = true;

    if (padre) {
      const p = parseCoded(padre);
      if (!p) { warnings.push(`Padre sin código válido: "${padre}"`); }
      else {
        currentParent = { id: cid(p.code), name: p.name, code: p.code, children: [] };
        parents.push(currentParent);
        comercialCodes.add(p.code);
        currentChild = null;
      }
    }
    if (comercial) {
      const c = parseCoded(comercial);
      if (!c) { warnings.push(`Comercial sin código válido: "${comercial}"`); }
      else if (!currentParent) { warnings.push(`Comercial "${comercial}" sin padre previo.`); }
      else {
        currentChild = { id: cid(c.code), name: c.name, code: c.code, associated: [] };
        currentParent.children!.push(currentChild);
        comercialCodes.add(c.code);
      }
    }
    if (elemento) {
      if (!currentChild) { warnings.push(`Elemento "${elemento}" sin categoría comercial previa.`); return; }
      const coded = parseCoded(elemento);
      if (coded) {
        // Reference to another commercial category (same tree) by code.
        currentChild.associated!.push(coded.code);
        refs++;
      } else {
        // Internal LIBRE-VIDA leaf: dedup + associate the child to it.
        const s = toSlug(elemento);
        if (!s) { warnings.push(`Elemento no representable: "${elemento}"`); return; }
        if (!leafBySlug.has(s)) leafBySlug.set(s, elemento.trim());
        currentChild.associated!.push({ tree: internoId, id: s });
        hojaLinks++;
      }
    }
  });

  // Warn about commercial refs that don't match any commercial code in the file.
  for (const p of parents) {
    for (const c of p.children ?? []) {
      for (const a of c.associated ?? []) {
        if (typeof a === 'string' && !comercialCodes.has(a)) {
          warnings.push(`Referencia "${a}" (en ${c.code}) no existe como categoría comercial en el fichero.`);
        }
      }
    }
  }

  const internaNodes: ImpNode[] = [...leafBySlug.entries()]
    .sort((a, b) => a[1].localeCompare(b[1], 'es'))
    .map(([s, name]) => ({ id: s, name }));

  // Clean empty associated arrays.
  for (const p of parents) for (const c of p.children ?? []) if (!c.associated?.length) delete c.associated;

  const payload: ImpPayload = {
    orgSlug,
    domain: { id: domainId, name: 'Categorizacion de Productos', icon: 'tags' },
    trees: [
      // Internal tree FIRST so the commercial tree's leaf associations resolve.
      { id: internoId, name: internoName, base_permission: 'organization', nodes: internaNodes },
      { id: comercialId, name: comercialName, base_permission: 'organization', nodes: parents },
    ],
  };

  const stats = {
    padres: parents.length,
    hijos: parents.reduce((a, p) => a + (p.children?.length ?? 0), 0),
    internas: internaNodes.length,
    refs,
    hojaLinks,
  };

  return { payload, stats, warnings };
}

// ---- Downloadable example template ----------------------------------------
export async function downloadMetadataTreeTemplate(): Promise<void> {
  const ExcelJSMod: any = await import('exceljs');
  const ExcelJS = ExcelJSMod.default || ExcelJSMod;
  const wb = new ExcelJS.Workbook();

  const cfg = wb.addWorksheet('Config');
  cfg.columns = [{ width: 22 }, { width: 40 }];
  const cfgRows: [string, string][] = [
    ['org_slug', 'balneario-de-alicun'],
    ['domain', 'categorization'],
    ['comercial_id', 'alicun-comercial'],
    ['comercial_nombre', 'Alicún — Comercial LIBRE-VIDA'],
    ['interno_id', 'alicun-interna'],
    ['interno_nombre', 'Alicún — Interna LIBRE-VIDA'],
  ];
  cfgRows.forEach((r) => cfg.addRow(r));
  cfg.getColumn(1).font = { bold: true };

  const ws = wb.addWorksheet('Árbol');
  ws.columns = [
    { header: 'Padre', width: 34 },
    { header: 'Comercial', width: 34 },
    { header: 'Elemento', width: 40 },
  ];
  ws.getRow(1).font = { bold: true };
  const rows: [string, string, string][] = [
    ['1 Experiencias saludables', '', ''],
    ['', '1.01 Salud y Termalismo', 'Balneoterapia'],
    ['', '', 'Indicaciones Tratamientos Termales'],
    ['', '', 'Piscinas Termales'],
    ['', '1.02 Escapadas saludables', 'Escapadas a la naturaleza'],
    ['', '', 'Escapadas en familia'],
    ['', '1.05 Experiencias saludables', '6.04 Terapias naturales'],
    ['', '', '1.02 Escapadas saludables'],
    ['', '', 'Talleres saludables'],
    ['', '', 'Cursos'],
  ];
  rows.forEach((r) => ws.addRow(r));

  const help = wb.addWorksheet('Instrucciones');
  help.columns = [{ width: 100 }];
  [
    'CÓMO RELLENAR ESTE FICHERO (copia el documento tal cual):',
    '',
    '1) Hoja "Config": datos del árbol (normalmente ya vienen bien; cambia org_slug si es otra organización).',
    '2) Hoja "Árbol": tres columnas, una fila por elemento, como en el documento:',
    '   • Columna "Padre": la categoría padre con su número (p.ej. "1 Experiencias saludables"). Solo en su fila.',
    '   • Columna "Comercial": la subcategoría con su número (p.ej. "1.01 Salud y Termalismo").',
    '   • Columna "Elemento": lo que cuelga de esa subcategoría, uno por fila:',
    '       - Si EMPIEZA por un número (p.ej. "6.04 Terapias naturales") → es una asociación a esa otra categoría.',
    '       - Si es TEXTO normal (p.ej. "Balneoterapia") → es una categoría interna (se crea y se asocia sola).',
    '',
    'No tienes que pensar en ids ni en dos árboles: el sistema los genera solo.',
    'Al subirlo verás un resumen (dry-run) antes de confirmar la creación.',
  ].forEach((line) => help.addRow([line]));

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla-arbol-metadatos.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}
